// ---------------------------------------------------------------------------
// C59: Knowledge Base CRUD Hooks
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface KbArticle {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  status: "draft" | "published";
  createdAt: string;
  updatedAt: string;
}

export interface KbArticlePagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export function useKbArticles(search?: string, page = 1, limit = 20) {
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [pagination, setPagination] = useState<KbArticlePagination>({
    total: 0,
    limit,
    offset: 0,
    hasMore: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    params.set("limit", String(limit));
    params.set("offset", String((Math.max(page, 1) - 1) * limit));
    const qs = `?${params.toString()}`;
    try {
      const res = await apiFetch<{ data: KbArticle[]; pagination?: KbArticlePagination }>(`/crm/knowledge-base${qs}`);
      setArticles(res.data ?? []);
      setPagination(res.pagination ?? {
        total: res.data?.length ?? 0,
        limit,
        offset: (Math.max(page, 1) - 1) * limit,
        hasMore: false,
      });
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load articles");
    } finally {
      setIsLoading(false);
    }
  }, [limit, page, search]);

  useEffect(() => { fetch(); }, [fetch]);
  return { articles, pagination, isLoading, error, refetch: fetch };
}

export async function createKbArticle(data: { title: string; content: string; category: string; tags: string[] }): Promise<KbArticle> {
  const res = await apiFetch<{ data: KbArticle }>("/crm/knowledge-base", { method: "POST", body: JSON.stringify(data) });
  return res.data;
}

export async function updateKbArticle(id: string, data: Partial<KbArticle>): Promise<KbArticle> {
  const res = await apiFetch<{ data: KbArticle }>(`/crm/knowledge-base/${id}`, { method: "PUT", body: JSON.stringify(data) });
  return res.data;
}

export async function getKbArticle(id: string): Promise<KbArticle> {
  const res = await apiFetch<{ data: KbArticle }>(`/crm/knowledge-base/${id}`);
  return res.data;
}

export async function deleteKbArticle(id: string): Promise<void> {
  await apiFetch(`/crm/knowledge-base/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// C60: Website Crawler
// ---------------------------------------------------------------------------

export interface CrawlConfig {
  url: string;
  maxDepth: number;
  maxPages: number;
  excludePatterns: string[];
  respectRobotsTxt: boolean;
}

export interface CrawlResult {
  id: string;
  status: "pending" | "crawling" | "completed" | "failed";
  pagesFound: number;
  articlesCreated: number;
  startedAt: string;
  completedAt?: string;
}

export async function startCrawl(config: CrawlConfig): Promise<CrawlResult> {
  const res = await apiFetch<{ data: CrawlResult }>("/crm/knowledge-base/crawl", { method: "POST", body: JSON.stringify(config) });
  return res.data;
}

export function useCrawlStatus(crawlId: string | undefined) {
  const [status, setStatus] = useState<CrawlResult | null>(null);

  useEffect(() => {
    if (!crawlId) return;
    const poll = setInterval(async () => {
      try {
        const res = await apiFetch<{ data: CrawlResult }>(`/crm/knowledge-base/crawl/${crawlId}`);
        setStatus(res.data);
        if (res.data.status === "completed" || res.data.status === "failed") clearInterval(poll);
      } catch { clearInterval(poll); }
    }, 3000);
    return () => clearInterval(poll);
  }, [crawlId]);

  return status;
}

// ---------------------------------------------------------------------------
// C61: Document Upload
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// C62: Vector Search (pgvector)
// ---------------------------------------------------------------------------

export interface SearchResult {
  articleId: string;
  title: string;
  snippet: string;
  similarity: number; // 0-1 cosine similarity
  category: string;
}

export async function searchKb(query: string): Promise<SearchResult[]> {
  const res = await apiFetch<{ data: SearchResult[] }>(`/crm/knowledge-base/search?q=${encodeURIComponent(query)}`);
  return res.data ?? [];
}

export function useKbSearch(query: string | undefined) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!query || query.length < 3) { setResults([]); return; }
    setIsSearching(true);
    searchKb(query)
      .then(setResults)
      .catch(() => setResults([]))
      .finally(() => setIsSearching(false));
  }, [query]);

  return { results, isSearching };
}

// ---------------------------------------------------------------------------
// C63: KB as AI Context — retrieve relevant articles for AI prompts
// ---------------------------------------------------------------------------

export async function getKbContextForQuery(query: string, maxArticles = 3): Promise<string> {
  const results = await searchKb(query);
  if (results.length === 0) return "";

  return results
    .slice(0, maxArticles)
    .map((r) => `[KB: ${r.title}] ${r.snippet}`)
    .join("\n\n");
}

// ---------------------------------------------------------------------------
// C61: Document Upload
// ---------------------------------------------------------------------------

export async function uploadDocument(file: File): Promise<{ articleId: string; title: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiFetch<{ data: { articleId: string; title: string } }>("/crm/knowledge-base/upload", {
    method: "POST",
    body: formData,
  });
  return response.data;
}
